import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, post, patch, del, toastError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section } from "@/components/common/Page";
import { DataTable } from "@/components/common/DataTable";
import { EnumSelect } from "@/components/common/Pickers";
import { ROLES, label } from "@/lib/labels";
import { fmtDateTime, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Users, ShieldCheck, Search, KeyRound } from "lucide-react";

const EMPTY = { name: "", email: "", password: "", role: "salg", phone: "", title: "", active: true };

const PERM_GROUPS = [
  ["dashboard", "Oversikt"], ["leads", "Henvendelser"], ["customers", "Kunder"], ["quotes", "Tilbud"], ["calculations", "Kalkyler"], ["projects", "Prosjekter"], ["planning", "Planlegging"],
  ["modules", "Moduler"], ["transport", "Transport"], ["service", "Service"], ["issues", "Avvik"], ["tasks", "Oppgaver"], ["suppliers", "Leverandører"], ["documents", "Dokumenter"],
  ["rates", "Satser"], ["users", "Brukere"], ["audit", "Revisjonslogg"], ["ai", "AI"], ["field", "Felt"], ["notifications", "Varsler"],
];

const FormRow = ({ label: l, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{l}</Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

const UserSheet = ({ open, onOpenChange, user: target }) => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const isEdit = !!target?.id;
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [seenKey, setSeenKey] = useState(null);
  const key = open ? target?.id || "new" : null;
  if (key !== seenKey) {
    setSeenKey(key);
    if (open) setForm(target ? { name: target.name || "", email: target.email || "", password: "", role: target.role || "salg", phone: target.phone || "", title: target.title || "", active: target.active !== false } : EMPTY);
  }
  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        const body = { name: form.name, email: form.email, role: form.role, phone: form.phone || null, title: form.title || null, active: !!form.active };
        if (form.password) body.password = form.password;
        return patch(`/users/${target.id}`, body);
      }
      return post("/users", { name: form.name, email: form.email, password: form.password, role: form.role, phone: form.phone || null, title: form.title || null, active: !!form.active });
    },
    onSuccess: () => { toast.success(isEdit ? "Bruker oppdatert" : "Bruker opprettet"); qc.invalidateQueries({ queryKey: ["users"] }); onOpenChange(false); },
    onError: (e) => toastError(e, "Kunne ikke lagre bruker"),
  });
  const isSelf = isEdit && me?.id === target.id;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto" data-testid="user-sheet">
        <SheetHeader>
          <SheetTitle className="">{isEdit ? `Rediger bruker` : "Ny bruker"}</SheetTitle>
          <SheetDescription>{isEdit ? "Endre rolle, kontaktinfo eller tilgang. Passord settes kun om feltet fylles ut." : "Brukeren får tilgang i henhold til valgt rolle."}</SheetDescription>
        </SheetHeader>
        <form className="space-y-4 py-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <FormRow label="Navn"><Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="user-name-input" /></FormRow>
          <FormRow label="E-post"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="user-email-input" /></FormRow>
          <div className="grid grid-cols-2 gap-3">
            <FormRow label="Telefon"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="user-phone-input" /></FormRow>
            <FormRow label="Tittel"><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="F.eks. Prosjektleder" data-testid="user-title-input" /></FormRow>
          </div>
          <FormRow label="Rolle" hint={isSelf ? "Du kan ikke endre din egen rolle her." : undefined}>
            <div className={isSelf ? "pointer-events-none opacity-60" : ""}>
              <EnumSelect value={form.role} onChange={(v) => set("role", v)} options={ROLES} labelFn={label} testId="user-role-select" />
            </div>
          </FormRow>
          <FormRow label={isEdit ? "Nytt passord (valgfritt)" : "Passord"} hint="Minst 6 tegn">
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required={!isEdit} minLength={6} autoComplete="new-password" data-testid="user-password-input" />
          </FormRow>
          <div className="flex items-center gap-2">
            <Switch id="user-active" checked={!!form.active} onCheckedChange={(v) => set("active", v)} disabled={isSelf} data-testid="user-active-switch" />
            <Label htmlFor="user-active" className="text-sm">Aktiv bruker</Label>
          </div>
          <SheetFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
            <Button type="submit" disabled={save.isPending} data-testid="user-save-button">{save.isPending ? "Lagrer…" : "Lagre"}</Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};

const RoleMatrix = ({ roles }) => {
  const permsOf = (r) => new Set(r.permissions || []);
  const has = (r, group, action) => { const p = permsOf(r); return p.has("*") || p.has(`${group}:${action}`); };
  return (
    <div className="overflow-x-auto rounded-md border bg-card" data-testid="role-matrix">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Område</th>
            {roles.map((r) => <th key={r.code} className="px-3 py-2 font-medium text-center whitespace-nowrap">{r.label || label(r.code)}</th>)}
          </tr>
        </thead>
        <tbody>
          {PERM_GROUPS.map(([g, gl]) => (
            <tr key={g} className="border-t">
              <td className="px-3 py-1.5 font-medium">{gl}</td>
              {roles.map((r) => {
                const read = has(r, g, "read"); const write = has(r, g, "write"); const approve = has(r, g, "approve");
                return (
                  <td key={r.code} className="px-3 py-1.5 text-center">
                    {write || approve ? <Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-0 font-normal">{approve && !write ? "Godkjenne" : "Les/skriv"}</Badge> : read ? <Badge variant="outline" className="font-normal">Les</Badge> : <span className="text-muted-foreground/40">–</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function UsersPage() {
  const { can, user: me } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("brukere");
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [edit, setEdit] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const users = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => get("/roles") });

  const rows = useMemo(() => (users.data || []).filter((u) => (role === "all" || u.role === role) && (!q || `${u.name} ${u.email} ${u.title || ""}`.toLowerCase().includes(q.toLowerCase()))), [users.data, role, q]);

  const toggleActive = useMutation({
    mutationFn: (u) => patch(`/users/${u.id}`, { active: !(u.active !== false) }),
    onSuccess: (_d, u) => { toast.success(u.active !== false ? "Bruker deaktivert" : "Bruker aktivert"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toastError(e),
  });
  const remove = useMutation({
    mutationFn: (u) => del(`/users/${u.id}`),
    onSuccess: () => { toast.success("Bruker slettet"); qc.invalidateQueries({ queryKey: ["users"] }); setConfirmDelete(null); },
    onError: (e) => { toastError(e, "Kunne ikke slette bruker"); setConfirmDelete(null); },
  });

  const columns = [
    { key: "name", header: "Bruker", render: (u) => (
      <div className="flex items-center gap-3">
        <Avatar className="h-8 w-8"><AvatarFallback className="text-[11px] bg-primary/10 text-primary font-semibold">{initials(u.name)}</AvatarFallback></Avatar>
        <div className="min-w-0"><div className="font-medium truncate">{u.name} {u.id === me?.id && <span className="text-xs text-muted-foreground">(deg)</span>}</div><div className="text-xs text-muted-foreground truncate">{u.email}</div></div>
      </div>
    ) },
    { key: "role", header: "Rolle", render: (u) => <Badge variant="outline" className="font-normal">{label(u.role)}</Badge> },
    { key: "title", header: "Tittel / telefon", render: (u) => <div className="text-xs"><div>{u.title || "–"}</div><div className="text-muted-foreground">{u.phone || ""}</div></div> },
    { key: "last_login", header: "Sist innlogget", render: (u) => <span className="text-xs text-muted-foreground tabular">{u.last_login_at ? fmtDateTime(u.last_login_at) : "–"}</span> },
    { key: "active", header: "Status", render: (u) => (u.active === false ? <span className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-slate-300" aria-hidden />Deaktivert</span> : <StatusBadge status="aktiv" />) },
    ...(can("users:write") ? [{ key: "actions", header: "", align: "right", render: (u) => (
      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEdit(u)} aria-label="Rediger" data-testid={`user-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={u.id === me?.id} onClick={() => toggleActive.mutate(u)} aria-label={u.active === false ? "Aktiver" : "Deaktiver"} title={u.active === false ? "Aktiver" : "Deaktiver"} data-testid={`user-toggle-active-${u.id}`}><KeyRound className="h-3.5 w-3.5" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" disabled={u.id === me?.id} onClick={() => setConfirmDelete(u)} aria-label="Slett" data-testid={`user-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    ) }] : []),
  ];

  const roleCounts = ROLES.map((r) => ({ r, n: (users.data || []).filter((u) => u.role === r && u.active !== false).length }));

  return (
    <div className="page-pad space-y-4 fade-up" data-testid="users-page">
      <PageHeader title="Brukere og roller" subtitle="Tilgangsstyring for salg, prosjekt, flåte, felt og ledelse" actions={can("users:write") && <Button onClick={() => setEdit({})} data-testid="user-create-button"><Plus className="h-4 w-4 mr-1" /> Ny bruker</Button>} />
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-9" data-testid="users-tabs">
            <TabsTrigger value="brukere" data-testid="users-tab-brukere"><Users className="h-4 w-4 mr-1" /> Brukere ({(users.data || []).length})</TabsTrigger>
            <TabsTrigger value="roller" data-testid="users-tab-roller"><ShieldCheck className="h-4 w-4 mr-1" /> Rollematrise</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "brukere" && (
          <>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk navn eller e-post" className="pl-8 h-9 w-56" data-testid="users-search-input" />
            </div>
            <EnumSelect value={role} onChange={setRole} options={ROLES} labelFn={label} allLabel="Alle roller" className="w-44 h-9" testId="users-role-filter" />
          </>
        )}
      </div>

      {tab === "brukere" ? (
        <>
          <div className="flex flex-wrap gap-1.5">
            {roleCounts.map(({ r, n }) => (
              <button key={r} type="button" onClick={() => setRole(role === r ? "all" : r)} aria-pressed={role === r} className={`inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] transition-colors duration-150 ${role === r ? "border-foreground/30 bg-muted font-medium" : "border-input hover:bg-muted"}`} data-testid={`users-chip-${r}`}>
                {label(r)} <span className="opacity-70 tabular">{n}</span>
              </button>
            ))}
          </div>
          <DataTable columns={columns} rows={rows} isLoading={users.isLoading} error={users.error} onRetry={users.refetch} onRowClick={can("users:write") ? (u) => setEdit(u) : undefined} empty="Ingen brukere matcher filteret" testId="users-table" rowTestId={(u) => `user-row-${u.id}`} />
        </>
      ) : (
        <div className="space-y-4">
          {roles.isLoading ? <div className="text-sm text-muted-foreground">Laster roller…</div> : <RoleMatrix roles={roles.data || []} />}
          <Section title="Rollebeskrivelser">
            <div className="grid gap-3 md:grid-cols-3 text-sm">
              {(roles.data || []).map((r) => (
                <div key={r.code} className="rounded-md border p-3" data-testid={`role-card-${r.code}`}>
                  <div className="flex items-center justify-between"><div className="font-medium">{r.label || label(r.code)}</div><Badge variant="outline" className="font-mono text-[10px]">{r.code}</Badge></div>
                  <div className="text-xs text-muted-foreground mt-1">{(r.permissions || []).includes("*") ? "Full tilgang til alle funksjoner og innstillinger." : `${(r.permissions || []).length} rettigheter · ${(r.permissions || []).filter((p) => p.endsWith(":write")).length} skrivetilganger`}</div>
                </div>
              ))}
            </div>
          </Section>
        </div>
      )}

      <UserSheet open={edit !== null} onOpenChange={(o) => !o && setEdit(null)} user={edit?.id ? edit : null} />
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Brukeren mister tilgang umiddelbart. Historikk og revisjonslogg beholdes. Vurder å deaktivere i stedet.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => remove.mutate(confirmDelete)} data-testid="confirm-delete-button">Slett bruker</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
